<template>
    <div class="main-container overflow-auto pa-5">
        <div class="content-container">
            <div class="date">{{ record.created.formatIf('dd, DD.MM.YYYY') }}</div>
            <h1>{{ record.title }}</h1>
            <div class="header-image">
                <figure>
                    <v-img :src="getHeaderImageUrl()" alt="Header-Image" max-width="750px" />
                    <figcaption>
                        <div>{{ imageRec ? imageRec.credits : '' }}</div>
                        <div>{{ imageRec ? imageRec.caption : '' }}</div>
                    </figcaption>
                </figure>
            </div>

            <!-- Content blocks -->
            <div class="content">
                <div v-for="(block, i) in contentBlocks.models" :key="block.id">
                    <div class="add-btn mb-15 mt-15">
                        <ActionBtn @click="insertBlockBefore(block, 'html')">
                            <KpIcon icon-cls="new" color="white" />
                            {{ $t('add_block') }}
                        </ActionBtn>
                        <!-- <content-block-choose @choose="insertBlockBefore(block, $event)" /> -->
                    </div>
                    <div class="content-block">
                        <div class="tools">
                            <icon-btn
                                icon-cls="trash"
                                icon-color="grey"
                                :tooltip="$t('delete')"
                                @click="onDelBtn(block)"
                            />
                            <icon-btn
                                v-if="i > 0"
                                icon-cls="arrowup"
                                icon-color="grey"
                                :tooltip="$t('move_up')"
                                @click="onUpBtn(block)"
                            />
                            <icon-btn
                                v-if="i < contentBlocks.length - 1"
                                icon-cls="arrowdown"
                                icon-color="grey"
                                :tooltip="$t('move_down')"
                                @click="onDownBtn(block)"
                            />
                        </div>
                        <!-- html fields are tinymce with special configs -->
                        <content-block-field
                            v-if="tinyActive && (block.type === 'html' || block.type === 'html_legacy')"
                            v-model="block.content"
                            :record="record"
                            context="magazin"
                            class="field"
                            :type="block.type"
                            :init="{
                                body_id: 'magazinPage_get',
                                body_class: 'magazin mce-editor-body',
                                toolbar1:
                                    'blocks | bold italic underline | alignleft aligncenter alignright | bullist numlist | cut copy paste',
                                toolbar2:
                                    'template | link unlink image media kpDocument | code fullscreen preview |undo redo',
                            }"
                        />
                        <!-- other fields, simpler variants -->
                        <content-block-field
                            v-else-if="tinyActive"
                            v-model="block.content"
                            class="field"
                            :type="block.type"
                            :init="{
                                body_id: 'magazinPage_get',
                                body_class: 'magazin mce-editor-body',
                            }"
                        />
                    </div>
                </div>
                <div v-if="!loading" class="add-btn">
                    <ActionBtn @click="insertBlockBefore(null, 'html')">
                        <KpIcon icon-cls="new" color="white" />
                        {{ $t('add_block') }}
                    </ActionBtn>
                    <!-- <content-block-choose @choose="insertBlockBefore(null, $event)" /> -->
                </div>
            </div>
            <div v-if="loading" class="ma-16 text-center">
                <v-progress-circular :size="50" color="primary" indeterminate />
            </div>
        </div>
    </div>
</template>

<script>
import { mapActions } from 'vuex';
import { Magazin } from '@/models/Magazin';
import { ContentBlockCollection } from '@/models/ContentBlock';
import GlobalsMixin from '@/mixins/globals';
import ContentBlockField from '@/components/form/content-block-field.vue';
import { instance as api } from '@/helpers/api';
import { errToStr } from '@/helpers/tools';

export default {
    name: 'MagazinEditContents',
    components: { ContentBlockField },
    mixins: [GlobalsMixin],
    props: {
        record: {
            type: Magazin,
            required: true,
        },
    },

    data() {
        return {
            loading: false,
            contentBlocks: new ContentBlockCollection(),
            // Attention, Hack ahead! See comment in loadContentBlocks:
            // we use it here to re-render the editor in the NEXT run loop
            // when re-rendering:
            tinyActive: true,
        };
    },

    computed: {
        imageRec() {
            return this.$store.getters['image/imageById'](this.record.featured_image_id);
        },
    },
    created() {
        this.cacheBuster = new Date().getTime();
    },

    mounted() {
        this.loadImage({ imageId: this.record.featured_image_id });
        this.loadContentBlocks();
    },

    methods: {
        ...mapActions('image', ['loadImage']),
        getImageConfig() {
            return this.globalVar('featured_images_config').magazin;
        },

        getImageUrl(magazinRec, imageConf) {
            if (!imageConf) {
                return '';
            }
            return `${this.globalVar('imageroot')}/magazin/${magazinRec.id}/${imageConf.ident}.${imageConf.format}?ts=${
                this.cacheBuster
            }`;
        },

        getHeaderImageUrl() {
            if (this.record) {
                return this.getImageUrl(this.record, this.getImageConfig()?.header) || {};
            }
            return null;
        },

        async loadContentBlocks() {
            this.loading = true;
            // Attention: Hack Ahead!
            // It seems that re-rendering a TinyMCE instance (in a v-for-loop)
            // does somehow break the editor: So we don't render it in the same
            // run loop, but wait for it (tinyActive will be used to hide (v-if) the editor above)
            await this.saveAllDirtyBlocks();
            this.tinyActive = false;
            await this.contentBlocks
                .setRemoteSort({
                    property: 'order_nr',
                    direction: 'ASC',
                })
                .addExtraParam('magazin_id', this.record.id || -1)
                .query();
            // ... see Hack above: now we show the editor
            this.$nextTick(() => (this.tinyActive = true));
            this.loading = false;
        },

        /**
         * Creates (inserts) a new block of the given type, before the
         * given existing block. If the given block is null, the block is inserted last.
         */
        async insertBlockBefore(contentBlock, type) {
            try {
                this.loading = true;
                await api().post('/contentBlock/create', {
                    magazin_id: this.record.id,
                    type,
                    before_block_id: contentBlock ? contentBlock.id : null,
                });
                this.loadContentBlocks();
            } catch (e) {
                this.$toast({ message: errToStr(e), color: 'error' });
            } finally {
                this.loading = false;
            }
        },

        /**
         * Callback for hooking into the save process:
         * called from the parent component to wait for this cmp to do things before the main
         * record is saved.
         *
         * return false to cancel the save process
         */
        async beforeSave() {
            await this.saveAllDirtyBlocks();
            return true;
        },

        async saveAllDirtyBlocks() {
            await Promise.all(
                this.contentBlocks.map(async (m) => {
                    if (m.isDirty()) {
                        await m.save();
                    }
                }),
            );
        },

        async onDelBtn(block) {
            if (block && confirm(this.$t('confirm_rec_delete'))) {
                try {
                    this.loading = true;
                    await block.delete();
                    this.loadContentBlocks();
                } catch (e) {
                    this.$toast({ message: errToStr(e), color: 'error' });
                } finally {
                    this.loading = false;
                }
            }
        },

        onUpBtn(block) {
            let block2Index = this.contentBlocks.indexOf(block);
            let block1Index = block2Index - 1;

            if (block1Index >= 0 && block2Index < this.contentBlocks.length) {
                let block1 = this.contentBlocks.models[block1Index];
                let block2 = this.contentBlocks.models[block2Index];
                this.swapBlocks(block1, block2);
            }
        },

        onDownBtn(block) {
            let block1Index = this.contentBlocks.indexOf(block);
            let block2Index = block1Index + 1;

            if (block1Index >= 0 && block2Index < this.contentBlocks.length) {
                let block1 = this.contentBlocks.models[block1Index];
                let block2 = this.contentBlocks.models[block2Index];
                this.swapBlocks(block1, block2);
            }
        },

        async swapBlocks(block1, block2) {
            let tmpOrder = block1.order_nr;
            block1.order_nr = block2.order_nr;
            block2.order_nr = tmpOrder;
            try {
                this.loading = true;
                await Promise.all([block1.save(), block2.save()]);
                this.loadContentBlocks();
            } catch (e) {
                this.$toast({ message: errToStr(e), color: 'error' });
            } finally {
                this.loading = false;
            }
        },
    },
};
</script>

<style lang="scss" scoped>
.main-container {
    display: flex;
    flex-direction: column;
    align-items: center;
}
.content-container {
    max-width: 785px;
}
.add-btn {
    display: flex;
    justify-content: center;
    margin: 10px;
}
.content-block {
    display: flex;
    flex-direction: row;
    gap: 5px;
    border: 1px dashed var(--border-color);
    padding: 5px;

    .field {
        flex-grow: 1;
    }

    .tools {
        display: flex;
        flex-direction: column;
    }
}
</style>
